// =============================================================================
// features/admin/feature-flags/StatusChangeConfirmDialog.tsx — Confirmação
// obrigatória ao promover uma flag `internal_only` → `enabled` (F38-S16).
//
// É a única transição que amplia a audiência: o avaliador do backend ignora
// a audiência por completo quando o status é `enabled` (audienceImpact.ts).
// Um clique que promovesse isso sem aviso — como o toggle binário fazia —
// vira exposição acidental de funcionalidade em teste (às vezes com PII de
// cidadão atrás da flag). Padrão de modal espelhado em
// features/ai-actions/components/RevertConfirmModal.tsx.
// =============================================================================

import * as React from 'react';

import { Button } from '../../../components/ui/Button';
import type { FeatureFlagDto } from '../../../hooks/admin/useFeatureFlags.types';
import { ROLE_LABELS } from '../../../hooks/admin/useUsers.types';
import { useCitiesList } from '../../../hooks/useCitiesList';
import { cn } from '../../../lib/cn';

import { audienceToDraft, buildAudienceImpact, describePromotionImpact } from './audienceImpact';

interface StatusChangeConfirmDialogProps {
  flag: FeatureFlagDto;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}

function ModalOverlay({ onClose }: { onClose: () => void }): React.JSX.Element {
  return (
    <div
      className="fixed inset-0 z-40"
      style={{ background: 'rgba(10,18,40,0.5)', backdropFilter: 'blur(2px)' }}
      onClick={onClose}
      aria-hidden="true"
    />
  );
}

export function StatusChangeConfirmDialog({
  flag,
  onConfirm,
  onCancel,
  isPending,
}: StatusChangeConfirmDialogProps): React.JSX.Element {
  const { cities } = useCitiesList();

  React.useEffect(() => {
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !isPending) onCancel();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isPending, onCancel]);

  const dialogRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const cityLabel = (id: string): string => cities.find((c) => c.id === id)?.name ?? id;
  const roleLabel = (key: string): string => ROLE_LABELS[key] ?? key;
  const impact = buildAudienceImpact('internal_only', audienceToDraft(flag.audience), {
    roleLabel,
    cityLabel,
  });
  const promotionText = describePromotionImpact(impact);

  return (
    <>
      <ModalOverlay onClose={isPending ? () => undefined : onCancel} />

      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="status-promote-title"
        aria-describedby="status-promote-desc"
        tabIndex={-1}
        className={cn(
          'fixed z-50 inset-4 md:inset-auto',
          'md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2',
          'md:w-[min(520px,90vw)]',
          'flex flex-col rounded-lg border border-border',
          'focus:outline-none',
        )}
        style={{
          background: 'var(--bg-elev-1)',
          boxShadow: 'var(--elev-5)',
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between p-5 border-b border-border shrink-0">
          <div>
            <h2
              id="status-promote-title"
              className="font-display font-bold text-ink"
              style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
            >
              Habilitar para todo mundo?
            </h2>
            <code className="font-mono text-xs text-azul">{flag.key}</code>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className={cn(
              'flex items-center justify-center w-8 h-8 rounded-md shrink-0',
              'text-ink-3 hover:text-ink hover:bg-surface-hover',
              'transition-colors duration-fast',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
            aria-label="Cancelar e fechar"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* ── Corpo ─────────────────────────────────────────────────── */}
        <div className="p-5 flex flex-col gap-4">
          <p
            id="status-promote-desc"
            className="font-sans text-sm text-ink"
            style={{ lineHeight: 1.5 }}
          >
            Esta flag está <strong className="font-semibold">Interno</strong> hoje. Passar para{' '}
            <strong className="font-semibold">Habilitado</strong> ignora a audiência configurada por
            completo — não é o outro lado de um interruptor, é abrir para todos os usuários
            autenticados.
          </p>

          <div
            className="flex items-start gap-3 p-3 rounded-md border"
            style={{ background: 'var(--warning-bg)', borderColor: 'var(--warning)' }}
            role="alert"
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              className="w-4 h-4 shrink-0 mt-0.5"
              style={{ color: 'var(--warning)' }}
              aria-hidden="true"
            >
              <path d="M8 1.5 1 14h14L8 1.5Z" strokeLinejoin="round" />
              <path d="M8 6v4M8 12v.5" strokeLinecap="round" />
            </svg>
            <p className="font-sans text-xs leading-relaxed" style={{ color: 'var(--warning)' }}>
              {promotionText}
            </p>
          </div>
        </div>

        {/* ── Footer ────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-border shrink-0">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
            Cancelar
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Habilitando…' : 'Habilitar para todos'}
          </Button>
        </div>
      </div>
    </>
  );
}
